import React, { useRef } from "react";
import { Helmet } from "react-helmet-async";
import Footer from "../../components/Footer";
import { useArticlesContext } from "../../context/ArticlesContext";
import MobileMapOverlay from "../../components/MobileMapOverlay";
import SocialMedia from "../../components/SocialMedia";
import Marker from "../../components/Marker";
import Top from "../../components-top/Top";
import Left from "../../components-top/Left";
import Right from "../../components-top/Right";
import Bottom from "../../components-top/Bottom";

export default function ArticleBestSushiVancouver() {
  // 1) Provide a unique reference ID (the slug in your ArticlesContext)
  const ref_id = "best-sushi-vancouver";

  // 2) Access articles from your context (optional usage)
  const { articles } = useArticlesContext();
  const ref_data = articles.find((art) => art.slug === ref_id);

  // =========================================================
  // ========== SECTION TITLES IDS (Labels Map) ==============
  // =========================================================
  const labelsMap = {
    intro: "Introduction",
    "section-1": "High-End & Omakase Experiences",
    "section-2": "Fusion & Casual Sushi",
    "section-3": "Richmond Hidden Gems",
    "section-4": "Budget-Friendly Spots",
    "dietary": "Vegetarian & Gluten-Free Options",
    faq: "FAQ",
    "local-regulations": "Local Regulations",
    conclusion: "Conclusion",
    references: "References",
  };

  // ===================================
  // 4) References Data
  // ===================================
  const referencesData = [
    {
      url: "https://www.destinationvancouver.com/",
      label: "Destination Vancouver (Official Tourism Board)",
    },
    {
      url: "https://www.ocean.org/seafood/",
      label: "Ocean Wise Sustainable Seafood Program",
    },
    {
      url: "https://shizenya.ca/",
      label: "Shizen Ya (Gluten-Free & Vegan-Friendly Sushi)",
    },
  ];

  // ===================================
  // 5) Locations for the Leaflet map
  // ===================================
  const locations = [
    {
      id: "miku",
      lat: 49.28683,
      lng: -123.11258,
      popupText: "Miku - Downtown Waterfront",
    },
    {
      id: "maumi",
      lat: 49.28900,
      lng: -123.13200,
      popupText: "Sushi Bar Maumi - West End",
    },
    {
      id: "the-eatery",
      lat: 49.26400,
      lng: -123.18170,
      popupText: "The Eatery - Kitsilano",
    },
    {
      id: "sushi-hachi",
      lat: 49.18447,
      lng: -123.12673,
      popupText: "Sushi Hachi - Richmond",
    },
    {
      id: "toshi-sushi",
      lat: 49.25671,
      lng: -123.10190,
      popupText: "Toshi Sushi - Mount Pleasant",
    },
  ];

  // Keep references to textual sections for scroll linking
  const spotRefs = useRef({});
  const setSpotRef = (id, el) => {
    spotRefs.current[id] = el;
  };

  // Desktop map reference
  const mapRef = useRef(null);

  // MOBILE MAP
  const mobileMapRef = useRef(null);

  return (
    <>
      {/* ============ SEO & METADATA ============ */}
      <Helmet>
        <title>Best Sushi in Vancouver - A Comprehensive Guide</title>
        <meta
          name="description"
          content="Discover the best sushi spots in Vancouver for every budget and style, including omakase bars, fusion sushi, and vegetarian-friendly options."
        />
        <meta
          name="keywords"
          content="sushi, Vancouver, omakase, budget-friendly, fusion, vegetarian, gluten-free"
        />
        <meta name="robots" content="index, follow" />
        <link
          rel="canonical"
          href="https://www.bcouver.com/articles/best-sushi-vancouver.html"
        />

        {/* Open Graph / Facebook */}
        <meta
          property="og:title"
          content="Best Sushi in Vancouver - A Comprehensive Guide"
        />
        <meta
          property="og:description"
          content="Explore Vancouver's top sushi experiences, from high-end omakase to casual fusion spots. "
        />
        <meta
          property="og:image"
          content="https://bcouver.com/assets/images/best-sushi-vancouver.jpg"
        />
        <meta
          property="og:url"
          content="https://www.bcouver.com/articles/best-sushi-vancouver.html"
        />
        <meta property="og:type" content="article" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Best Sushi in Vancouver - A Comprehensive Guide"
        />
        <meta
          name="twitter:description"
          content="Learn about Vancouver's top sushi places, sustainable seafood, local tips, and more."
        />
        <meta
          name="twitter:image"
          content="https://bcouver.com/assets/images/best-sushi-vancouver.jpg"
        />
      </Helmet>

      {/* ============ TOP NAVBAR + HERO ============ */}
      <Top ref_id={ref_id} />

      <main>
        <div className="container article-main">
          {/* LEFT COLUMN (TableOfContents + Map) */}
          <Left
            labelsMap={labelsMap}
            locations={locations}
            mapRef={mapRef}
            spotRefs={spotRefs}
          />

          {/* ============ ARTICLE CONTENT ============ */}
          <article className="article-content">
            {/* Featured Image */}
            <img
              src={
                ref_data?.image ||
                "https://bcouver.com/assets/images/article-fallback.jpg"
              }
              alt="Fresh sushi arrangement on a plate"
              className="featured-image"
            />

            {/* Social Share */}
            <SocialMedia />

            {/* SECTION: Introduction */}
            <h2 id="intro">Introduction</h2>
            <p>
              Vancouver boasts one of the most diverse and vibrant sushi scenes
              outside of Japan. From high-end omakase experiences to casual
              sushi bars and creative fusion rolls, the city has something for
              every taste and budget. This guide explores top-rated spots
              throughout Vancouver’s neighborhoods—Downtown, Kitsilano,
              Richmond, and beyond—offering practical tips on sustainability,
              dietary options, and local dining etiquette.
            </p>

            {/* SECTION: High-End & Omakase */}
            <h2 id="section-1">High-End & Omakase Experiences</h2>
            <p>
              For a truly immersive dining experience, treat yourself to an
              omakase meal. Vancouver's top sushi chefs import fresh fish from
              Japan while also highlighting local catches. Expect artful plating
              and meticulous attention to detail.
            </p>
            <p
              ref={(el) => setSpotRef("miku", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Miku (Downtown)</strong>{" "}
              <Marker
                coordY={49.28683}
                coordX={-123.11258}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              – Located at 70-200 Granville Street, this upscale waterfront
              restaurant pioneered aburi (flame-seared) sushi in Vancouver and
              is known for its signature Salmon Oshi Sushi. Expect to spend
              around $50–$80 per person. Open Mon–Fri (11:30am–10:00pm) and
              Sat–Sun (12:00pm–10:00pm).
            </p>
            <p
              ref={(el) => setSpotRef("maumi", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Sushi Bar Maumi (West End)</strong>{" "}
              <Marker
                coordY={49.28900}
                coordX={-123.13200}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              – Tucked away at 1668 Robson Street, this exclusive omakase spot
              only seats about ten diners at a time. Expect to book in advance
              and budget around $100+ per person. The intimate chef-led meal
              showcases the freshest seasonal fish with precise, traditional
              preparation.
            </p>

            {/* SECTION: Fusion & Casual Sushi */}
            <h2 id="section-2">Fusion & Casual Sushi</h2>
            <p>
              Vancouverites love creative sushi twists. Fusion spots experiment
              with local ingredients, playful flavors, and vibrant atmospheres.
              Whether you want a laid-back night out or a unique take on classic
              rolls, these casual restaurants deliver on taste and experience.
            </p>
            <p
              ref={(el) => setSpotRef("the-eatery", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>The Eatery (Kitsilano)</strong>{" "}
              <Marker
                coordY={49.26400}
                coordX={-123.18170}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              – At 3431 West Broadway, this eclectic spot offers funky decor,
              a neon-lit atmosphere, and boldly named fusion rolls. Expect
              inventive ingredients like smoked salmon with cream cheese or a
              spicy mango drizzle. Prices range $25–$40. Open Mon–Thu
              (4:30pm–10:00pm), Fri (3:30pm–11:00pm), Sat (12:00pm–11:00pm),
              Sun (12:00pm–10:00pm).
            </p>

            {/* SECTION: Richmond Hidden Gems */}
            <h2 id="section-3">Richmond Hidden Gems</h2>
            <p>
              Richmond, just south of Vancouver, is renowned for its authentic
              Asian dining. Many top-quality sushi restaurants occupy modest
              strip malls, but the fresh fish and expert craftsmanship keep
              aficionados coming back for more.
            </p>
            <p
              ref={(el) => setSpotRef("sushi-hachi", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Sushi Hachi (Richmond)</strong>{" "}
              <Marker
                coordY={49.18447}
                coordX={-123.12673}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              – Nestled in #1278-8888 Odlin Crescent, this humble, family-run
              restaurant focuses purely on sushi and sashimi—no extra frills.
              Expect a quiet ambiance and deeply traditional flavors. Open
              Wed–Sat (5:00pm–9:00pm); closed Sun, Mon, Tue. Prices about
              $40–$60 per person; cash only.
            </p>

            {/* SECTION: Budget-Friendly Spots */}
            <h2 id="section-4">Budget-Friendly Spots</h2>
            <p>
              Vancouver’s sushi remains top-notch even for those dining on a
              budget. For a no-frills experience at a reasonable price, check
              out these beloved local staples. 
            </p>
            <p
              ref={(el) => setSpotRef("toshi-sushi", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Toshi Sushi (Mount Pleasant)</strong>{" "}
              <Marker
                coordY={49.25671}
                coordX={-123.10190}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              – Found at 181 E 16th Avenue, Toshi is famous for fresh fish, big
              portions, and reasonable prices (~$20–$30 per person). Beware
              lines at opening (4:30pm) as they don’t take reservations. Closed
              Sunday and Monday; a community favorite for years.
            </p>

            {/* SECTION: Dietary Considerations */}
            <h2 id="dietary">Vegetarian & Gluten-Free Options</h2>
            <p>
              Sushi in Vancouver isn’t just for seafood lovers. Many restaurants
              cater to vegetarian, vegan, and gluten-free diets. You’ll find
              rolls made with avocado, cucumber, or yam tempura. Gluten-free
              tamari often replaces regular soy sauce upon request. Restaurants
              like Miku and The Eatery mark menu items for dietary restrictions,
              and spots like Shizen Ya even serve organic brown rice rolls with
              clearly labeled vegan/gluten-free selections.
            </p>

            {/* SECTION: FAQ */}
            <h2 id="faq">FAQ</h2>
            <div className="faq-section">
              <h3>1. How expensive is sushi in Vancouver?</h3>
              <p>
                Sushi ranges widely—from ~$20 per person at budget spots like
                Toshi Sushi to $100+ for omakase at Sushi Bar Maumi. Mid-range
                places often run $30–$50, depending on what you order.
              </p>

              <h3>2. Do I need reservations?</h3>
              <p>
                Many high-end or intimate sushi bars (like Maumi) require
                reservations. Casual spots (Toshi Sushi, for example) usually
                operate on a first-come, first-served basis, so arrive early if
                you want to avoid a wait.
              </p>

              <h3>3. Are there late-night sushi options?</h3>
              <p>
                Most sushi restaurants close by 9 or 10pm. However, some
                locations, like The Eatery, stay open until 11pm on weekends,
                offering a more lively night scene.
              </p>

            </div>

            {/* SECTION: Local Regulations */}
            <h2 id="local-regulations">Local Regulations</h2>
            <p>
              Vancouver maintains strict food safety standards through regular
              health inspections. Many restaurants participate in the
              Ocean Wise program to support sustainable seafood practices.
              Tipping 15–20% is standard. Adherence to any posted health
              guidelines (e.g., if masking is requested) is appreciated.
            </p>

            {/* SECTION: Conclusion */}
            <h2 id="conclusion">Conclusion</h2>
            <p>
              Whether you crave a delicate omakase experience or a fun night out
              with fusion rolls, Vancouver’s sushi scene will not disappoint.
              High-quality seafood, sustainable practices, and diverse culinary
              creativity make it a top destination for sushi lovers worldwide.
              Explore a few of these recommended spots, and you’ll likely
              discover new favorites along the way.
            </p>
            <p>
              <em>
                Disclaimer: This guide is for informational purposes only.
                Always check official resources, including restaurant websites,
                for the latest menus, hours, and safety considerations.
              </em>
            </p>

            {/* BOTTOM COMPONENT: references, author, related articles, etc. */}
            <Bottom referencesData={referencesData} ref_data={ref_data} />
          </article>

          {/* RIGHT SIDE COLUMN */}
          <Right currentArticle={ref_data} />
        </div>
      </main>

      {/* MOBILE MAP OVERLAY */}
      <MobileMapOverlay
        ref={mobileMapRef}
        markers={locations}
        spotRefs={spotRefs}
      />

      <Footer />
    </>
  );
}